/**
 * SessionDetailView - Displays historical conversation events for a past session.
 *
 * Reuses ConversationPanel and ToolUsagePanel with the same layout as ConversationView.
 * Events are fetched from GET /api/sessions/{session_id}/events and transformed
 * into the same data shapes used by the live conversation components.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "@cloudscape-design/components/button";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Box from "@cloudscape-design/components/box";
import Spinner from "@cloudscape-design/components/spinner";
import Alert from "@cloudscape-design/components/alert";
import ConversationPanel from "./ConversationPanel";
import ToolUsagePanel from "./ToolUsagePanel";
import ScenarioHeader from "./ScenarioHeader";
import { getSessionEvents } from "../services/sessionApi";
import { getScenario } from "../services/scenarioApi";
import { formatDuration } from "./SessionsView";
import "./ConversationView.css";

/**
 * Transform raw API session events into messages and toolInvocations arrays
 * matching the shapes expected by ConversationPanel and ToolUsagePanel.
 *
 * @param {Array} events - Raw session events from the API
 * @returns {{ messages: Array, toolInvocations: Array }}
 */
export function transformEvents(events) {
  const messages = [];
  const toolInvocations = [];

  for (const event of events) {
    if (event.event_type === "user_speech" || event.event_type === "ai_response") {
      messages.push({
        event_id: event.event_id,
        type: event.event_type,
        content: event.content,
        timestamp: event.timestamp,
        metadata: event.metadata,
      });
    } else if (event.event_type === "tool_use") {
      let toolName = event.metadata?.tool_name || "Unknown Tool";
      let input = {};
      try {
        if (event.content) {
          const parsed = JSON.parse(event.content);
          toolName = parsed.tool_name || toolName;
          input = parsed.input || {};
        }
      } catch { /* use defaults */ }
      toolInvocations.push({
        event_id: event.event_id,
        tool_name: toolName,
        input,
        status: "pending",
        timestamp: event.timestamp,
      });
    } else if (event.event_type === "tool_result") {
      const toolName = event.metadata?.tool_name;
      const target = toolInvocations.find(
        (t) => t.tool_name === toolName && t.status === "pending"
      );
      if (target) {
        target.output = event.metadata?.output || event.content;
        target.status = event.metadata?.error ? "error" : "success";
        target.error = event.metadata?.error;
        target.result_timestamp = event.timestamp;
      }
    }
  }

  return { messages, toolInvocations };
}

function SessionDetailView({ session, onBack }) {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scenario, setScenario] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSessionEvents(session.session_id);
      setEvents(data || []);
    } catch (err) {
      setError(err.message || t("session-detail.errors.fetch_failed"));
    } finally {
      setLoading(false);
    }
  }, [session.session_id, t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (session.scenario_id) {
      getScenario(session.scenario_id)
        .then((data) => setScenario(data))
        .catch(() => {}); // silently fail — header will just be missing
    }
  }, [session.scenario_id]);

  const { messages, toolInvocations } = useMemo(
    () => transformEvents(events),
    [events]
  );

  const formattedStartTime = new Date(session.created_at).toLocaleString(i18n.language);
  const formattedDuration = formatDuration(session.created_at, session.updated_at);
  const callingNumber = session.metadata?.from_number || "\u2014";
  const calledNumber = session.metadata?.to_number || "\u2014";

  return (
    <main className="conversation-view">
      <div className="conversation-view-header">
        <Button variant="link" iconName="arrow-left" onClick={onBack}>
          {t("session-detail.header.back_to_sessions")}
        </Button>
      </div>

      <ScenarioHeader scenario={scenario} />

      <SpaceBetween direction="horizontal" size="m">
        <Box>{t("session-detail.header.start_time")}: {formattedStartTime}</Box>
        <Box>{t("session-detail.header.duration")}: {formattedDuration}</Box>
        <Box>{t("session-detail.header.calling_number")}: {callingNumber}</Box>
        <Box>{t("session-detail.header.called_number")}: {calledNumber}</Box>
      </SpaceBetween>

      {error && (
        <Alert
          type="error"
          action={
            <Button onClick={fetchEvents}>{t("errors.api.try_again")}</Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spinner size="large" />
          <Box variant="p" margin={{ top: "s" }}>
            {t("session-detail.view.loading")}
          </Box>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <Box textAlign="center" padding={{ vertical: "xxl" }}>
          <SpaceBetween size="m">
            <Box variant="h2" color="text-body-secondary">
              {t("session-detail.view.empty_title")}
            </Box>
            <Box variant="p" color="text-body-secondary">
              {t("session-detail.view.empty_message")}
            </Box>
          </SpaceBetween>
        </Box>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="conversation-split-panel">
          <ConversationPanel messages={messages} />
          <ToolUsagePanel toolInvocations={toolInvocations} />
        </div>
      )}
    </main>
  );
}

export default SessionDetailView;
